import { useEffect, useMemo, useState } from "react";
import { useWidgetDetailsContext } from "../../WidgetDetailsContextProvider";
import _ from "lodash";
import { getMultilingualString } from "shared/utils";
import { useWidgetUpdateByIdMutation, WidgetByIdDocument } from "gql/generated";
import { useToastContext } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";

type ComboBoxTag = {
  _id: string;
  label: string;
  value: string;
  count: number;
};

type ComboBoxTagGroup = {
  key: string;
  tags: ComboBoxTag[];
} & ComboBoxTag;

export const useWidgetSetup = () => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const { widget } = useWidgetDetailsContext();

  // Keep track of the selected filterable tags in a state so we can update
  // the UI while the update mutation is occuring
  const [selectedFilterableTags, setSelectedFilterableTags] = useState<
    string[]
  >([]);
  const [operatorsHaveNoFilters, setOperatorsHaveNoFilters] = useState(false);
  useEffect(() => {
    if (!widget) {
      return;
    }
    setSelectedFilterableTags(
      widget.filterableTags.map((tag) => tag && tag._id),
    );
  }, [widget, setSelectedFilterableTags]);

  // Get all of the unique tag IDs from All of the entities in the widget
  // Note, the entityCountPerTagId value is not currently displayed in the UI
  // because the label property in ComboBox.Item only accepts plain text
  // TODO : Maybe update ComboBox.Item in Trekker to support a child instead
  // TODO : of just text ... That way, the label used in the Tag can remain
  // TODO : just the label and the larger display can be a Flex or something
  const { uniqueTags, entityCountPerTagId } = useMemo(() => {
    // Get all of the unique tag Ids from the entities
    const uniqueTags = _.uniqBy(
      [
        ...(widget.list.entities?.flatMap((entity) =>
          entity.tags?.businessType.map((tag) => tag),
        ) ?? []),
        ...(widget.list.entities?.flatMap((entity) =>
          entity.tags?.organizationSpecific.map((tag) => tag),
        ) ?? []),
        ...(widget.list.entities?.flatMap((entity) =>
          entity.tags?.global.map((tag) => tag),
        ) ?? []),
        ...(widget.list.entities?.flatMap((entity) =>
          entity.tags?.amenity.map((tag) => tag),
        ) ?? []),
      ],
      "_id",
    ).filter((tag) => tag !== null && tag !== undefined);

    setOperatorsHaveNoFilters(uniqueTags.length === 0);

    // Get the entity counts for each of the tag Ids
    const entityCountPerTagId: { [key: string]: number } = {};
    uniqueTags.forEach((tag) => {
      entityCountPerTagId[tag._id] =
        widget.list.entities?.filter((entity) => {
          const entityTags = [
            ...(entity.tags?.businessType.map((tag) => tag._id) ?? []),
            ...(entity.tags?.global.map((tag) => tag._id) ?? []),
            ...(entity.tags?.organizationSpecific.map((tag) => tag._id) ?? []),
            ...(entity.tags?.amenity.map((tag) => tag._id) ?? []),
          ];
          return entityTags.indexOf(tag._id) > -1;
        }).length || 0;
    });

    return {
      uniqueTags,
      entityCountPerTagId,
    };
  }, [widget.list.entities, setOperatorsHaveNoFilters]);

  /**
   * Convert the flat list of tags into an array of tag groups
   * - The Groups will be used as the <Combobox.ItemGroup> and <Combobox.ItemGroupLabel>
   *   and are currently non-selectable
   * - The tags within each group are selectable
   */
  const [filterableTags, setFilterableTags] = useState<ComboBoxTagGroup[]>([]);
  const allTagGroupsAndItems = useMemo(() => {
    const tagGroups = _.sortBy(
      Array.from(
        new Map(
          uniqueTags.map((tag) => [
            tag.tagGroup?._id,
            {
              ...tag.tagGroup,
              label: getMultilingualString(tag.tagGroup?.name ?? {}),
              value: tag.tagGroup?._id,
              count: 0,
              tags: [],
            },
          ]),
        ).values(),
      ),
      "label",
    );

    const tagsGroupsAndItems = tagGroups.map((tagGroup) => ({
      ...tagGroup,
      tags: _.sortBy(
        uniqueTags
          .filter((tag) => (tag.tagGroup?._id ?? "") === tagGroup._id)
          .map((tag) => ({
            ...tag,
            label: getMultilingualString(tag.name),
            value: tag._id,
            count: entityCountPerTagId[tag._id],
          })),
        "label",
      ),
    }));

    setFilterableTags(tagsGroupsAndItems as ComboBoxTagGroup[]);
    return tagsGroupsAndItems;
  }, [uniqueTags, entityCountPerTagId]);

  // Filter the list items as the user types
  const onTagInputValueSearch = (val: string) => {
    setFilterableTags(
      !val
        ? (allTagGroupsAndItems as ComboBoxTagGroup[])
        : (allTagGroupsAndItems
            .map((tagGroup) => ({
              ...tagGroup,
              tags: tagGroup.tags.filter(
                (tag) =>
                  tag.label.toLowerCase().indexOf(val.toLowerCase()) > -1,
              ),
            }))
            .filter(
              (tagGroup) => tagGroup.tags.length > 0,
            ) as ComboBoxTagGroup[]),
    );
  };

  /**
   * When the selected tags are changed, save the changes right away
   * - While the is happening, update the local widget state to make it look faster
   *   ...and revert if an error occurs
   * @param filterableTags
   */
  const [
    widgetUpdateByIdMutation,
    { error: errorUpdateMutation, loading: loadingUpdateMutation },
  ] = useWidgetUpdateByIdMutation();

  const handleUpdateSelectedTagIds = (filterableTags: string[]) => {
    if (!widget || loadingUpdateMutation) {
      return;
    }

    // Copy the current list, then update the current list to the updated list
    const filterableTagsBeforeUpdate = [...selectedFilterableTags];
    setSelectedFilterableTags(filterableTags);

    widgetUpdateByIdMutation({
      variables: {
        id: widget?._id,
        record: {
          filterableTags,
        },
      },
      refetchQueries: [WidgetByIdDocument],
    })
      .then((response) => {
        if (response.errors) {
          // Reset the local state
          setSelectedFilterableTags(filterableTagsBeforeUpdate);
          toastFactory.create({
            title: t("core:ERROR.TITLE"),
            description: `${t("widgets:DETAILS.TABS.VIEW_SUBSECTIONS.SETUP.FILTERS.ERROR")}. ${t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
          });
        }
      })
      .catch(() => {
        // Reset the local state
        setSelectedFilterableTags(filterableTagsBeforeUpdate);
        toastFactory.create({
          title: t("core:ERROR.TITLE"),
          description: `${t("widgets:DETAILS.TABS.VIEW_SUBSECTIONS.SETUP.FILTERS.ERROR")}. ${t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
        });
      });
  };

  return {
    allTagGroupsAndItems,
    errorUpdateMutation,
    filterableTags,
    loadingUpdateMutation,
    operatorsHaveNoFilters,
    selectedTagIds: selectedFilterableTags,
    handleUpdateSelectedTagIds,
    onTagInputValueSearch,
  };
};
